import { Mail, Phone, MapPin } from "lucide-react";
import { useContactModal } from "@/context/ContactModalContext";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/lib/content";

const Contact = () => {
  const { open } = useContactModal();

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="relative border-b border-hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-0 lg:pt-0 pb-6 lg:pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <span className="eyebrow">Contact —</span>
              <h1 className="mt-8 font-serif text-[44px] sm:text-[56px] lg:text-[64px] leading-[1.06] text-offwhite tracking-[-0.015em] max-w-[14ch]">
                Let's start a conversation
              </h1>
              <p className="mt-8 max-w-[44ch] text-[15px] leading-[1.8] text-offwhite/65">
                Tell us about your goals and challenges. We'll be in touch to
                explore how we can help.
              </p>
              <button
                onClick={open}
                data-testid="contact-page-cta"
                className="mt-10 inline-flex items-center px-7 py-3.5 bg-gold text-[#0A0A0A] text-[13px] tracking-[0.05em] font-medium hover:bg-[#D9BC85] transition-colors"
              >
                Start a conversation
              </button>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="relative aspect-[6/5] lg:min-h-[620px] overflow-hidden">
                <img
                  src={IMAGES.conferenceTable}
                  alt="Sable Advisory consultation lounge"
                  className="w-full h-full object-cover object-[48%_50%] brightness-[1.14] contrast-[1.04]"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-[#0A0A0A]/18" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/55 via-transparent to-[#0A0A0A]/45" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/55 via-transparent to-[#0A0A0A]/20" />
                <div className="absolute inset-0 shadow-[inset_0_0_90px_rgba(10,10,10,0.72)]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DETAILS */}
      <section
        data-testid="contact-details"
        className="border-b border-hairline bg-[#0B0B0B]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-16">
            <Reveal>
              <div data-testid="contact-detail-email">
                <div className="flex items-center gap-3 mb-5">
                  <Mail size={18} strokeWidth={1.4} className="text-gold" />
                  <span className="eyebrow">Email</span>
                </div>
                <a
                  href="mailto:info@sableadvisory.com"
                  className="font-serif text-[22px] text-offwhite hover:text-gold transition-colors"
                >
                  info@sableadvisory.com
                </a>
                <p className="mt-3 text-[13px] text-offwhite/55">
                  For general enquiries and new engagements.
                </p>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div data-testid="contact-detail-phone">
                <div className="flex items-center gap-3 mb-5">
                  <Phone size={18} strokeWidth={1.4} className="text-gold" />
                  <span className="eyebrow">Phone</span>
                </div>
                <a
                  href="tel:+35312345678"
                  className="font-serif text-[22px] text-offwhite hover:text-gold transition-colors"
                >
                  +353 1 234 5678
                </a>
                <p className="mt-3 text-[13px] text-offwhite/55">
                  Monday to Friday, 09:00–18:00 GMT.
                </p>
              </div>
            </Reveal>

            <Reveal delay={240}>
              <div data-testid="contact-detail-address">
                <div className="flex items-center gap-3 mb-5">
                  <MapPin size={18} strokeWidth={1.4} className="text-gold" />
                  <span className="eyebrow">Office</span>
                </div>
                <p className="font-serif text-[22px] text-offwhite leading-snug">
                  Sable House
                  <br />
                  Dublin 2, Ireland
                </p>
                <p className="mt-3 text-[13px] text-offwhite/55">
                  By appointment only.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FINAL NOTE */}
      <section className="border-b border-hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <p className="font-serif italic text-[22px] sm:text-[26px] lg:text-[30px] leading-[1.3] text-[#D4B66F] max-w-[40ch]">
                Whether you're shaping a new strategy, navigating regulation,
                or preparing for a transaction — we're here to listen first.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end lg:pr-8">
              <button
                onClick={open}
                data-testid="contact-final-cta"
                className="px-6 py-2.5 bg-black/20 border border-[#D8BD7A] text-[13px] font-medium tracking-[0.04em] text-[#D8BD7A] backdrop-blur-sm hover:bg-gold hover:border-gold hover:text-[#0A0A0A] transition-all duration-200"
              >
                Start a conversation
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
